import { Translate } from '@change-corgi/core/react/i18n';
import { ButtonLink } from '@change-corgi/design-system-legacy/components/actions';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import type { Channel } from '@change-corgi/design-system-legacy/components/social';
import { getSocialColor, getSocialIcon } from '@change-corgi/design-system-legacy/components/social';
import { Box } from '@change-corgi/design-system-legacy/layout';
import { createModalHook } from '@change-corgi/design-system-legacy/modals';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

function LiDot() {
	return (
		<span
			sx={{
				content: "'•'",
				position: 'absolute',
				left: 0,
				top: '0.4em',
				lineHeight: '1',
				fontWeight: 'bold',
			}}
			aria-hidden="true"
		>
			•
		</span>
	);
}

export const useTipSharingModal = createModalHook({
	closeButton: true,
	name: 'TipSharingModal',
	stylesOverrides: {
		width: ['auto', '632px'],
	},
	heading: () => (
		<>
			<Heading as="h5" pb={23}>
				<Translate value="corgi.petition.starter_dashboard.recommendations.sharing_tips" />
			</Heading>
			<Heading as="h2" size="h3">
				<Translate value="corgi.petition.starter_dashboard.recommendations.share_modal.title" />
			</Heading>
		</>
	),
	body: function TipSharingModal({ closeModal }) {
		const channel: Channel = 'facebook';

		return (
			<Box>
				<Heading mt={24} as="h3" size={['h5']} sx={{ lineHeight: '28px', display: 'flex' }}>
					<Icon icon={getSocialIcon(channel)} size="xxl" color={getSocialColor(channel)} mr={8} />
					<Translate value="corgi.petition.starter_dashboard.recommendations.facebook.title" />
				</Heading>
				<Box py={8} sx={{ position: 'relative' }}>
					<Translate value="corgi.petition.starter_dashboard.recommendations.facebook.description" />
				</Box>
				<Box sx={{ position: 'relative' }}>
					<ul sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
						{[...Array(4).keys()].map((index) => (
							<li
								key={`facebook-${index}`}
								sx={{
									position: 'relative',
									marginLeft: '8px',
									paddingLeft: '16px',
									listStyle: 'none',
								}}
							>
								<LiDot />
								<Text fontWeight="bold">
									<Translate
										value={`corgi.petition.starter_dashboard.recommendations.facebook.sharing_tips.tip_${index + 1}.label`}
									/>
									:&nbsp;
								</Text>
								<Translate
									value={`corgi.petition.starter_dashboard.recommendations.facebook.sharing_tips.tip_${index + 1}.text`}
								/>
							</li>
						))}
					</ul>
				</Box>

				<Heading mt={24} as="h3" size={['h5']} sx={{ lineHeight: '28px', display: 'flex' }}>
					<Icon
						icon={getSocialIcon('reddit')}
						size={24}
						backgroundColor={getSocialColor('reddit')}
						color="white"
						m={2}
						sx={{ lineHeight: '28px', borderRadius: '50%' }}
						mr={8}
					/>
					<Translate value="corgi.petition.starter_dashboard.recommendations.reddit.title" />
				</Heading>
				<Box py={8} sx={{ position: 'relative' }}>
					<Translate value="corgi.petition.starter_dashboard.recommendations.reddit.description" />
				</Box>
				<Box sx={{ position: 'relative' }}>
					<ul sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
						{[...Array(4).keys()].map((index) => (
							<li
								sx={{
									position: 'relative',
									marginLeft: '8px',
									paddingLeft: '16px',
									listStyle: 'none',
								}}
								key={`reddit-${index}`}
							>
								<LiDot />
								<Text fontWeight="bold">
									<Translate
										value={`corgi.petition.starter_dashboard.recommendations.reddit.sharing_tips.tip_${index + 1}.label`}
									/>
									:&nbsp;
								</Text>
								<Translate
									value={`corgi.petition.starter_dashboard.recommendations.reddit.sharing_tips.tip_${index + 1}.text`}
								/>
							</li>
						))}
					</ul>
				</Box>

				<Box mt={24} sx={{ display: 'flex', justifyContent: ['center', 'end'] }}>
					<ButtonLink to="#" onClick={closeModal} data-qa="close-tips-modal-button" sx={{ width: ['100%', 'auto'] }}>
						<Translate value="corgi.petition.starter_dashboard.recommendations.share_modal.cta" />
					</ButtonLink>
				</Box>
			</Box>
		);
	},
});
